import { FC } from "react";
import { useParams } from "react-router-dom";

import AccountTreeOutlinedIcon from "@mui/icons-material/AccountTreeOutlined";
import CloseIcon from "@mui/icons-material/Close";
import NumbersRoundedIcon from "@mui/icons-material/NumbersRounded";
import SaveIcon from "@mui/icons-material/Save";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Grid,
	IconButton,
	InputAdornment,
	Typography,
} from "@mui/material";
import { Form, Formik } from "formik";

import { Translate } from "@/components/Application/Translate";

import CustomInput from "@/helpers/components/CustomFormFields/CustomInput";

import { CreateNewProcedureNodeRequest } from "@/model/Procedure/ProcedureNodeDependency";

import { CreateNewProcedureNode } from "@/service/ProcedureMutations";
import { RouteParams } from "@/service/Utilities";
import { AlertToast } from "@/helpers/alerts/AlertToast";

interface NewProcedureNodeProps {
	open: boolean;
	onClose: () => void;
}

export const CreateNewProcedureNodeModal: FC<NewProcedureNodeProps> = ({
	open,
	onClose,
}) => {
	const { id } = useParams<RouteParams>();
	const createNewNode = CreateNewProcedureNode();

	const fieldSx = { "& .MuiInputBase-root": { height: 40, borderRadius: 2 } };
	const buttonSx = {
		height: 40,
		borderRadius: 2,
		textTransform: "none",
		fontWeight: 800,
	};

	const validate = (values: CreateNewProcedureNodeRequest) => {
		const errors: Partial<Record<keyof CreateNewProcedureNodeRequest, string>> =
			{};

		if (!values.Name?.trim()) {
			errors.Name = "Required";
		}

		if (values.NodeNumber?.trim() && !/^\d+$/.test(values.NodeNumber.trim())) {
			errors.NodeNumber = "Numbers only";
		}

		return errors;
	};

	const onSubmit = async (values: CreateNewProcedureNodeRequest) => {
		try {
			await createNewNode(values);
			AlertToast.success();
			onClose();
		} catch (error) {
			AlertToast.failed();
			console.error(error);
		}
	};

	return (
		<Dialog
			open={open}
			onClose={onClose}
			fullWidth
			maxWidth="sm"
			PaperProps={{ sx: { borderRadius: 3, overflow: "hidden" } }}
			aria-labelledby="create-node-title"
		>
			{/* Header */}
			<DialogTitle
				id="create-node-title"
				sx={{ pr: 6, pb: 0.5, fontWeight: 800 }}
			>
				<Translate
					HTMLTag="span"
					className="heading"
					String="Procedure.Node.Create.New"
				>
					Create New Node
				</Translate>
			</DialogTitle>

			{/* Close */}
			<IconButton
				aria-label="close"
				onClick={onClose}
				sx={{ position: "absolute", right: 8, top: 8 }}
			>
				<CloseIcon />
			</IconButton>

			<DialogContent dividers sx={{ pt: 1.5 }}>
				<Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
					<Translate String="Procedure.Node.Create.Subtitle">
						Add a node name and number to organize this procedure’s structure.
					</Translate>
				</Typography>

				<Formik<CreateNewProcedureNodeRequest>
					initialValues={{
						ProcedureId: id,
						Name: "",
						NodeNumber: "",
					}}
					validate={validate}
					onSubmit={onSubmit}
					validateOnMount
				>
					{({ isSubmitting, isValid, dirty }) => (
						<Form>
							<Grid container spacing={2}>
								<Grid item xs={12} md={6}>
									<CustomInput
										type="text"
										label="Name"
										name="Name"
										id="NodeName"
										translationString="Procedure.Node.Name"
										fullWidth
										autoFocus
										placeholder="e.g. Preparation"
										sx={fieldSx}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<AccountTreeOutlinedIcon fontSize="small" />
												</InputAdornment>
											),
										}}
									/>
								</Grid>

								<Grid item xs={12} md={6}>
									<CustomInput
										type="text"
										label="Node Number"
										name="NodeNumber"
										id="NodeNumber"
										translationString="Procedure.Node.Number"
										fullWidth
										placeholder="e.g. 12"
										sx={fieldSx}
										InputProps={{
											startAdornment: (
												<InputAdornment position="start">
													<NumbersRoundedIcon fontSize="small" />
												</InputAdornment>
											),
										}}
										onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
											e.target.value = e.target.value.replace(/\D/g, "");
										}}
									/>
								</Grid>
							</Grid>

							{/* Footer */}
							<DialogActions sx={{ pt: 2, mt: 0.5, px: 0, gap: 1 }}>
								<Box sx={{ flex: 1 }} />
								<Button
									variant="outlined"
									color="warning"
									startIcon={<CloseIcon />}
									onClick={onClose}
									sx={buttonSx}
								>
									<Translate String="Procedure.Button.Close">Close</Translate>
								</Button>
								<Button
									variant="contained"
									color="success"
									startIcon={<SaveIcon />}
									type="submit"
									disabled={isSubmitting || !isValid || !dirty}
									sx={buttonSx}
								>
									<Translate String="Procedure.Node.Create.Save">
										Save
									</Translate>
								</Button>
							</DialogActions>
						</Form>
					)}
				</Formik>
			</DialogContent>
		</Dialog>
	);
};
